import { Box, Button, Card, CardContent, Chip, Divider, Grid, Typography } from '@mui/material';
import { compact, find, first, join, map, size } from 'es-toolkit/compat';
import { AnnotationAssignment } from 'interfaces/annotation';
import React, { useState } from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';
import { useBiopsySiteTypes } from 'utils/queryHooks/uiConstantsHooks';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { EditAnnotationAssignmentDialog } from '../AnnotationAssignment/EditAnnotationAssignmentDialog';
import AnnotationAssignmentTable from './AnnotationAssignmentTable';
import { AnnotationAssignmentStatistics } from './AnnotationAssignmentTableToolbar';
import { AnnotationAssignmentTimelineChart } from './AnnotationAssignmentTimelineChart';

interface AnnotationAssignmentViewProps {
  annotationAssignment: AnnotationAssignment;
  studyId: string;
  fetchOnlySlidesOfStudy: boolean;
  withLegacy: boolean;
  scrollAnchorRef?: (element: HTMLDivElement | null) => void;
}

function areAnnotationAssignmentViewPropsEqual(
  previousProps: AnnotationAssignmentViewProps,
  nextProps: AnnotationAssignmentViewProps
) {
  return (
    previousProps.annotationAssignment === nextProps.annotationAssignment &&
    previousProps.studyId === nextProps.studyId &&
    previousProps.fetchOnlySlidesOfStudy === nextProps.fetchOnlySlidesOfStudy &&
    previousProps.withLegacy === nextProps.withLegacy
  );
}

const AnnotationAssignmentView: React.FC<AnnotationAssignmentViewProps> = ({
  annotationAssignment,
  studyId,
  fetchOnlySlidesOfStudy,
  withLegacy,
  scrollAnchorRef,
}) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { biopsySiteTypes } = useBiopsySiteTypes();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const assignmentClassesCount = size(first(annotationAssignment.todos)?.options ?? []);
  const assignmentSlidesCount =
    annotationAssignment.slideMetadataSummary?.totalSlideCount ?? size(annotationAssignment.annotations ?? []);
  const annotatedSlidesCount = annotationAssignment.slideMetadataSummary?.annotatedSlideCount;
  const stainSummaryLabel = join(
    map(annotationAssignment.slideMetadataSummary?.stainTypeCounts ?? [], ({ value, count }) => {
      return `${stainTypeIdToDisplayName(value)} (${count})`;
    }),
    ', '
  );
  const biopsySiteSummaryLabel = join(
    map(annotationAssignment.slideMetadataSummary?.biopsySiteCounts ?? [], ({ value, count }) => {
      return `${find(biopsySiteTypes, { id: Number(value) })?.displayName || value} (${count})`;
    }),
    ', '
  );

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  return (
    <>
      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ pb: 1.5 }}>
          <Grid container spacing={2} alignItems="flex-start" justifyContent="space-between">
            <Grid item xs>
              <Box ref={scrollAnchorRef} sx={{ scrollMarginTop: 24 }}>
                <Typography variant="h4">{`${annotationAssignment.annotationAssignmentId}- ${annotationAssignment.name}`}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {annotationAssignment.description ||
                    'Review classes, assigned slides, and annotation totals for this assignment.'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Chip
                    size="small"
                    color="secondary"
                    label={annotationAssignment.isGlobal ? 'Global assignment' : 'Study assignment'}
                  />
                  <Chip size="small" variant="outlined" label={`${assignmentClassesCount} classes`} />
                  <Chip size="small" variant="outlined" label={`${assignmentSlidesCount} slides`} />
                  {annotatedSlidesCount != null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${annotatedSlidesCount}/${assignmentSlidesCount} annotated slides`}
                    />
                  ) : null}
                  {annotationAssignment.slideMetadataSummary?.latestActivityAt ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Updated ${formatDateTimeForUser(
                        annotationAssignment.slideMetadataSummary.latestActivityAt
                      )}`}
                    />
                  ) : null}
                  {map(annotationAssignment.tags ?? [], (tag) => (
                    <Chip key={tag} size="small" variant="outlined" label={`tag:${tag}`} />
                  ))}
                </Box>
                {compact([stainSummaryLabel, biopsySiteSummaryLabel]).length ? (
                  <Box sx={{ mt: 2 }}>
                    {stainSummaryLabel ? (
                      <Typography variant="body2" color="text.secondary">
                        Stains: {stainSummaryLabel}
                      </Typography>
                    ) : null}
                    {biopsySiteSummaryLabel ? (
                      <Typography variant="body2" color="text.secondary">
                        Biopsy sites: {biopsySiteSummaryLabel}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid item>
              <Button variant="outlined" onClick={handleEditClick}>
                Edit assignment
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <AnnotationAssignmentStatistics annotationAssignment={annotationAssignment} showOverviewControls={false} />
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 2 }}>
          <AnnotationAssignmentTimelineChart annotationAssignment={annotationAssignment} />
        </Box>
        <Divider />
        <Box sx={{ px: 2, pb: 2 }}>
          <AnnotationAssignmentTable studyId={studyId} annotationAssignment={annotationAssignment} />
        </Box>
      </Card>

      {editDialogOpen && (
        <EditAnnotationAssignmentDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          annotationAssignmentId={annotationAssignment.annotationAssignmentId}
          fetchOnlySlidesOfStudy={fetchOnlySlidesOfStudy}
          withLegacy={withLegacy}
        />
      )}
    </>
  );
};

export default React.memo(AnnotationAssignmentView, areAnnotationAssignmentViewPropsEqual);
